import { useApp } from '../context.js';
import { FLOATING_EMOJI_OPTIONS, DEFAULT_FLOATING_EMOJI } from '../presets.js';
import { Camera, ChevronDown, ChevronLeft, Settings } from '../icons.jsx';

export default function SettingsPanel() {
    const { colorTheme, currentProfile, deletedRestaurants, floatingEmoji, handleLogout, haptic, profilePhotos, setColorTheme, setFloatingEmoji, setShowFloatingBg, setShowProfilePhotoUpload, setShowSettings, setShowTrash, setSortBy, showFloatingBg, showToast, sortBy, t } = useApp();
    return (
        <div className="pt-6 pb-24 animate-fadeIn">
            {/* Settings Header */}
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={() => setShowSettings(false)}
                    className={`flex items-center gap-1 ${t.accentText} font-semibold apple-button`}
                >
                    <ChevronLeft size={20} />
                    Back
                </button>
                <h1 className="text-xl font-bold text-gray-800">Settings</h1>
                <div className="w-16"></div>
            </div>

            {/* Profile Section */}
            <div className="bg-white/90 apple-card rounded-2xl shadow-md p-6 mb-4">
                <div className="flex flex-col items-center">
                    <button
                        onClick={() => setShowProfilePhotoUpload(currentProfile)}
                        className="relative mb-3"
                    >
                        {profilePhotos[currentProfile] ? (
                            <img
                                src={profilePhotos[currentProfile]}
                                alt={currentProfile}
                                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                            />
                        ) : (
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl border-4 border-white shadow-lg ${
                                currentProfile === 'doug'
                                    ? 'bg-gradient-to-br from-blue-400 to-indigo-500'
                                    : 'bg-gradient-to-br from-pink-400 to-rose-500'
                            }`}>
                                {currentProfile === 'doug' ? '💛' : '💖'}
                            </div>
                        )}
                        <div className={`absolute bottom-0 right-0 w-7 h-7 bg-gradient-to-r ${t.accent} rounded-full flex items-center justify-center shadow-md`}>
                            <Camera size={14} className="text-white" />
                        </div>
                    </button>
                    <h2 className="text-lg font-bold text-gray-800">
                        {currentProfile === 'doug' ? '💛 Doug' : '💖 Les'}
                    </h2>
                    <p className="text-sm text-gray-500 mb-3">Tap photo to change</p>
                    <button
                        onClick={() => { handleLogout(); setShowSettings(false); }}
                        className="bg-gray-100 text-gray-700 px-5 py-2 rounded-xl font-semibold text-sm apple-button hover:bg-gray-200"
                    >
                        Switch Profile
                    </button>
                </div>
            </div>

            {/* Appearance Section */}
            <div className="mb-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1 mb-2">Appearance</h3>
                <div className="bg-white/90 apple-card rounded-2xl shadow-md overflow-hidden">
                    {/* Theme Chooser */}
                    <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3 mb-2">
                            <span className="text-lg">🎨</span>
                            <span className="font-medium text-gray-700">Color Theme</span>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setColorTheme('classic');
                                    localStorage.setItem('colorTheme', 'classic');
                                    haptic('light');
                                }}
                                className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold apple-button ${
                                    colorTheme === 'classic'
                                        ? 'bg-gradient-to-r from-orange-400 to-red-400 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                                🍊 Classic
                            </button>
                            <button
                                onClick={() => {
                                    setColorTheme('profile');
                                    localStorage.setItem('colorTheme', 'profile');
                                    haptic('light');
                                }}
                                className={`flex-1 py-2 px-3 rounded-xl text-xs font-semibold apple-button ${
                                    colorTheme === 'profile'
                                        ? (currentProfile === 'les'
                                            ? 'bg-gradient-to-r from-pink-400 to-rose-500 text-white shadow-md'
                                            : 'bg-gradient-to-r from-amber-500 to-orange-400 text-white shadow-md')
                                        : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                                {currentProfile === 'les' ? '🌸 Rose' : '☕ Warm'}
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                            <span className="text-lg">{floatingEmoji || (currentProfile === 'les' ? DEFAULT_FLOATING_EMOJI.les : DEFAULT_FLOATING_EMOJI.doug)}</span>
                            <span className="font-medium text-gray-700">Floating Background</span>
                        </div>
                        <button
                            onClick={() => {
                                const next = !showFloatingBg;
                                setShowFloatingBg(next);
                                localStorage.setItem('showFloatingBg', String(next));
                                haptic('light');
                            }}
                            className={`w-12 h-7 rounded-full transition-colors relative ${
                                showFloatingBg ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                        >
                            <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                                showFloatingBg ? 'translate-x-5' : 'translate-x-0.5'
                            }`}></div>
                        </button>
                    </div>
                    {showFloatingBg && (
                        <div className="px-4 py-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-2">Floating Emoji</p>
                            <div className="flex gap-2 flex-wrap">
                                {(currentProfile === 'les'
                                    ? FLOATING_EMOJI_OPTIONS.les
                                    : FLOATING_EMOJI_OPTIONS.doug
                                ).map((emoji) => (
                                    <button
                                        key={emoji}
                                        onClick={() => {
                                            setFloatingEmoji(emoji);
                                            localStorage.setItem('floatingEmoji', emoji);
                                            haptic('light');
                                        }}
                                        className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center apple-button ${
                                            floatingEmoji === emoji
                                                ? (currentProfile === 'les'
                                                    ? 'bg-pink-100 ring-2 ring-pink-400 shadow-md'
                                                    : 'bg-amber-100 ring-2 ring-amber-600 shadow-md')
                                                : 'bg-gray-100'
                                        }`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Preferences Section */}
            <div className="mb-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1 mb-2">Preferences</h3>
                <div className="bg-white/90 apple-card rounded-2xl shadow-md overflow-hidden">
                    <div className="px-4 py-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                                <span className="text-lg">📋</span>
                                <span className="font-medium text-gray-700">Default Sort</span>
                            </div>
                        </div>
                        <select
                            value={sortBy}
                            onChange={(e) => {
                                setSortBy(e.target.value);
                                haptic('light');
                            }}
                            className="w-full px-3 py-2 bg-gray-100 rounded-xl text-gray-700 font-medium text-base border-0 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-light))]"
                        >
                            <option value="name">Alphabetical</option>
                            <option value="rating">Highest Rated</option>
                            <option value="recent">Recently Visited</option>
                            <option value="unvisited">Unvisited First</option>
                            <option value="topRatedUnvisited">Top Rated Unvisited</option>
                            <option value="favorites">Favorites First</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Data Section */}
            <div className="mb-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider px-1 mb-2">Data</h3>
                <div className="bg-white/90 apple-card rounded-2xl shadow-md overflow-hidden">
                    <button
                        onClick={() => { setShowTrash(true); setShowSettings(false); }}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-lg">🗑️</span>
                            <span className="font-medium text-gray-700">Trash</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {deletedRestaurants.length > 0 && (
                                <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full">
                                    {deletedRestaurants.length}
                                </span>
                            )}
                            <ChevronDown size={16} className="text-gray-400 -rotate-90" />
                        </div>
                    </button>
                </div>
            </div>

            {/* Reset to Default */}
            <div className="mb-4">
                <div className="bg-white/90 apple-card rounded-2xl shadow-md overflow-hidden">
                    <button
                        onClick={() => {
                            setShowFloatingBg(true);
                            setFloatingEmoji('');
                            setSortBy('name');
                            setColorTheme('classic');
                            localStorage.setItem('showFloatingBg', 'true');
                            localStorage.removeItem('floatingEmoji');
                            localStorage.setItem('sortPreference', 'name');
                            localStorage.setItem('colorTheme', 'classic');
                            haptic('medium');
                            showToast('Settings reset to default', 'success');
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-500 font-medium hover:bg-red-50 transition-colors"
                    >
                        Reset to Default
                    </button>
                </div>
            </div>

            {/* App Info */}
            <div className="text-center pt-4">
                <p className="text-xs text-gray-400">Our Restaurant Journey</p>
                <p className="text-xs text-gray-400">Made with love</p>
                <p className="text-[11px] text-gray-400 mt-1">Build {(typeof document !== 'undefined' && document.querySelector('meta[name="build"]')?.content) || 'dev'}</p>
            </div>
        </div>
    );
}
